import React, { useState, useEffect } from 'react';
import { axiosInstance } from '../../services/api';

/**
 * MysteryVault Component
 *
 * Displays a compact dark-gradient card showing that letters are waiting
 * for the user, without revealing who sent them or what they contain.
 *
 * Props:
 * - pendingCount: Number of pending letters (optional - will fetch if not provided)
 * - onCountLoaded: Callback when count is loaded
 */
const MysteryVault = ({ pendingCount: externalCount, onCountLoaded }) => {
  const [pendingCount, setPendingCount] = useState(externalCount || 0);
  const [loading, setLoading] = useState(externalCount === undefined);

  useEffect(() => {
    if (externalCount !== undefined) {
      setPendingCount(externalCount);
      return;
    }

    const fetchPendingCount = async () => {
      try {
        const response = await axiosInstance.get('/timecapsules/pending_count/');
        const count = response.data.pending_count || 0;
        setPendingCount(count);
        if (onCountLoaded) {
          onCountLoaded(count);
        }
      } catch (error) {
        console.error('Failed to fetch pending count:', error);
        setPendingCount(0);
      } finally {
        setLoading(false);
      }
    };

    fetchPendingCount();
  }, [externalCount, onCountLoaded]);

  if (loading) {
    return (
      <div className="rounded-2xl p-5 animate-pulse" style={{ backgroundColor: '#3a4a20' }}>
        <div className="h-6 rounded w-1/3 mb-2" style={{ backgroundColor: 'rgba(255,255,255,0.1)' }} />
        <div className="h-4 rounded w-2/3" style={{ backgroundColor: 'rgba(255,255,255,0.1)' }} />
      </div>
    );
  }

  if (pendingCount === 0) {
    return null;
  }

  return (
    <div
      className="rounded-2xl overflow-hidden relative flex items-center gap-5"
      style={{
        background: 'linear-gradient(135deg, #2E3B18 0%, #4A5D23 50%, #556B2F 100%)',
        padding: '20px 24px',
        boxShadow: '0 2px 0 0 rgba(255,255,255,0.06) inset, 0 8px 30px rgba(46,59,24,0.35), 0 2px 8px rgba(0,0,0,0.15)',
      }}
    >
      {/* Dot texture overlay */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          backgroundImage: 'radial-gradient(rgba(255,255,255,0.04) 1px, transparent 1px)',
          backgroundSize: '16px 16px',
        }}
      />

      {/* Shimmer sweep overlay */}
      <div
        className="absolute pointer-events-none"
        style={{
          top: '-50%',
          left: '-60%',
          width: '40%',
          height: '200%',
          background: 'linear-gradient(105deg, transparent 40%, rgba(255,255,255,0.07) 45%, rgba(255,255,255,0.13) 50%, rgba(255,255,255,0.07) 55%, transparent 60%)',
          animation: 'vaultShimmer 4s ease-in-out infinite',
        }}
      />

      {/* Shimmer keyframes */}
      <style>{`
        @keyframes vaultShimmer {
          0% { left: -60%; }
          100% { left: 130%; }
        }
        @keyframes vaultBreathe {
          0%, 100% { box-shadow: 0 0 0 0 rgba(255,255,255,0.05); }
          50% { box-shadow: 0 0 0 8px rgba(255,255,255,0.03); }
        }
      `}</style>

      {/* Left: Lock icon */}
      <div className="relative z-10 flex-shrink-0">
        <div
          className="w-[68px] h-[68px] rounded-full flex items-center justify-center relative"
          style={{
            background: 'rgba(255,255,255,0.08)',
            border: '2px solid rgba(255,255,255,0.12)',
            animation: 'vaultBreathe 3s ease-in-out infinite',
          }}
        >
          <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.65)" strokeWidth="1.8">
            <rect x="3" y="11" width="18" height="11" rx="2" />
            <path d="M7 11V7a5 5 0 0110 0v4" />
          </svg>
          {/* Count badge */}
          <div
            className="absolute flex items-center justify-center"
            style={{
              top: '-3px',
              right: '-3px',
              minWidth: '24px',
              height: '24px',
              borderRadius: '12px',
              background: '#E65100',
              color: '#fff',
              fontSize: '12px',
              fontWeight: 800,
              padding: '0 6px',
              border: '2.5px solid #2E3B18',
              boxShadow: '0 2px 8px rgba(230,81,0,0.4)',
            }}
          >
            {pendingCount}
          </div>
        </div>
      </div>

      {/* Center: Title, description, hint chips */}
      <div className="flex-1 relative z-10 text-white">
        <div
          style={{
            fontSize: '11px',
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: '1.2px',
            opacity: 0.5,
            marginBottom: '3px',
          }}
        >
          Mystery Vault
        </div>
        <div
          style={{
            fontFamily: "'Lora', serif",
            fontSize: '19px',
            fontWeight: 600,
            marginBottom: '4px',
          }}
        >
          {pendingCount} Letter{pendingCount !== 1 ? 's' : ''} Waiting
        </div>
        <div style={{ fontSize: '13px', opacity: 0.65, lineHeight: 1.4 }}>
          Family members have written you letters that will unlock at special moments in your life.
        </div>

        {/* Hint chips */}
        <div className="flex gap-3 mt-2.5 flex-wrap">
          <div
            className="inline-flex items-center gap-1.5 text-xs rounded-full"
            style={{
              padding: '5px 12px',
              background: 'rgba(255,255,255,0.08)',
              color: 'rgba(255,255,255,0.75)',
              backdropFilter: 'blur(2px)',
            }}
          >
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ opacity: 0.6, flexShrink: 0 }}>
              <circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" />
            </svg>
            Dates kept secret
          </div>
          <div
            className="inline-flex items-center gap-1.5 text-xs rounded-full"
            style={{
              padding: '5px 12px',
              background: 'rgba(255,255,255,0.08)',
              color: 'rgba(255,255,255,0.75)',
              backdropFilter: 'blur(2px)',
            }}
          >
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ opacity: 0.6, flexShrink: 0 }}>
              <path d="M20.84 4.61a5.5 5.5 0 00-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 00-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 000-7.78z" />
            </svg>
            From people who love you
          </div>
        </div>
      </div>

      {/* Right: Quote */}
      <div className="relative z-10 flex-shrink-0 text-center text-white hidden sm:block">
        <div
          style={{
            fontFamily: "'Lora', serif",
            fontSize: '13px',
            fontStyle: 'italic',
            opacity: 0.45,
            maxWidth: '120px',
            lineHeight: 1.4,
          }}
        >
          These moments will find you &#10022;
        </div>
      </div>
    </div>
  );
};

export default MysteryVault;
